import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MotionConfig } from "framer-motion";
import api from "@/api";
import { Reveal, MaskedLines, Overline, Magnetic } from "@/components/Motion";
import { useSettings, usePage } from "@/settings";

const VISION_POINTS = [
  { title: "Material exploration", text: "We prototype with earth, laterite, lime, brick, timber and terracotta — local, low-energy materials that stay honest to how they were made." },
  { title: "Climate-conscious design", text: "On the coast, shade, breeze and monsoon come first. Courtyards, jaali walls and deep overhangs do the work before any machine is switched on." },
  { title: "Craftsmanship", text: "Drawings, models and mock-ups are made by hand. We work alongside masons, carpenters and ceramic artists, treating their knowledge as design input." },
  { title: "Shared learning", text: "Through workshops and open making, what the studio learns is passed on — to students, collaborators and clients alike." },
];

function AnchorLinks({ showTeam }) {
  const links = [
    ["#who-we-are", "Who We Are", "about-anchor-who"],
    ["#vision", "Vision", "about-anchor-vision"],
    ["#founder", "Founder", "about-anchor-founder"],
  ];
  if (showTeam) links.push(["#team", "Team", "about-anchor-team"]);
  return (
    <nav data-testid="about-anchors" aria-label="Page sections"
      className="flex flex-wrap gap-x-8 gap-y-2 border-y border-ink/15 py-4 mt-14">
      {links.map(([href, label, testid]) => (
        <a key={href} href={href} data-testid={testid}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-brand transition-colors py-2">
          <span className="w-1.5 h-1.5 rounded-full bg-brand/60" /> {label}
        </a>
      ))}
    </nav>
  );
}

function WhoWeAre() {
  const s = useSettings();
  const pg = usePage("about");
  return (
    <section id="who-we-are" data-testid="about-who" className="scroll-mt-28 mt-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <Overline>IKSHA — Who We Are</Overline>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.12] mt-6">
            {pg.who_heading || "An emerging art and architecture studio based in Mangalore."}
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 lg:pt-16">
          <p className="text-lg text-ink/70 leading-relaxed">{s.about_intro}</p>
          <p className="mt-5 text-ink/60 leading-relaxed">
            {pg.who_text_2 || "We work across buildings, interiors, paintings, objects and workshops — exploring the connections between space, art, materials, craft and making, and letting each practice continually inform the other."}
          </p>
        </Reveal>
      </div>
      <Reveal className="mt-14">
        <figure>
          <div className="frame-clip spotlight aspect-[16/8]">
            <img src={pg.who_image || "/projects/dhara-4.webp"} alt={pg.who_image_alt || "Dhara project courtyard with curved laterite walls, Mangalore"}
              className="w-full h-full object-cover" loading="lazy" />
          </div>
          {(pg.who_image_caption ?? "Dhara — Sustainable Building Material Practice Institute, Mangalore · Designed by IKSHA") && (
            <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">
              {pg.who_image_caption ?? "Dhara — Sustainable Building Material Practice Institute, Mangalore · Designed by IKSHA"}
            </figcaption>
          )}
        </figure>
      </Reveal>
    </section>
  );
}

function Vision() {
  const pg = usePage("about");
  const points = pg.vision_points && pg.vision_points.length ? pg.vision_points : VISION_POINTS;
  return (
    <section id="vision" data-testid="about-vision" className="scroll-mt-28 mt-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <Overline>Our Vision</Overline>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.12] mt-6">
            {pg.vision_heading || "Thoughtful spaces and artworks, made with intent."}
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 lg:pt-16">
          <p className="text-ink/60 leading-relaxed max-w-xl">
            {pg.vision_intro || "Our ambition is specific: spaces and artworks that stay honest to their place, their climate and the hands that made them. Four commitments guide every project and piece:"}
          </p>
        </Reveal>
      </div>
      <div className="mt-14">
        {points.map((v, i) => (
          <Reveal key={v.title} delay={0.05}>
            <div data-testid={`vision-point-${i + 1}`}
              className={`grid grid-cols-12 gap-4 md:gap-6 py-8 border-t border-ink/15 ${i === points.length - 1 ? "border-b" : ""}`}>
              <span className="col-span-2 font-mono text-sm text-brand">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="col-span-10 md:col-span-4 font-display text-2xl md:text-3xl tracking-tight">{v.title}</h3>
              <p className="col-span-12 md:col-span-6 text-ink/60 leading-relaxed">{v.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function PlaceholderPhoto({ label, className = "" }) {
  return (
    <div className={`border border-dashed border-ink/30 bg-linen flex items-center justify-center p-6 ${className}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 text-center leading-relaxed">
        {label}
      </p>
    </div>
  );
}

function Founder({ founder }) {
  return (
    <section id="founder" data-testid="about-founder" className="scroll-mt-28 mt-28">
      <Reveal><Overline>Our Founder</Overline></Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10 items-start">
        <Reveal className="lg:col-span-4">
          {founder?.photo ? (
            <div className="frame-clip spotlight aspect-[3/4]">
              <img src={founder.photo} alt={founder.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ) : (
            <PlaceholderPhoto className="aspect-[3/4]" label={"Founder portrait —\nadd a photograph in Admin → Team"} />
          )}
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.12]">
            {founder?.name || <span className="text-ink/35">[ Founder name ]</span>}
          </h2>
          <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-brand">
            {founder?.role || "Founder, IKSHA"}
          </p>
          {founder?.bio ? (
            <p className="mt-8 text-ink/70 leading-relaxed max-w-2xl whitespace-pre-line">{founder.bio}</p>
          ) : (
            <div className="mt-8 max-w-2xl space-y-5 border border-dashed border-ink/30 bg-linen p-6 md:p-8">
              <p className="text-ink/50 leading-relaxed">
                [ Short biography — the founder's architectural education and how it meets their art
                practice. ]
              </p>
              <p className="text-ink/50 leading-relaxed">
                [ The story behind IKSHA — why the studio was founded, and what it set out to do. ]
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 pt-2 border-t border-ink/15">
                Editable placeholders — replace in Admin → Team
              </p>
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Team({ members }) {
  const pg = usePage("about");
  return (
    <section id="team" data-testid="about-team" className="scroll-mt-28 mt-28">
      <Reveal>
        <Overline>Our Team</Overline>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.12] mt-6">
          {pg.team_heading || "The people behind the work."}
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 mt-14">
        {members.map((m, i) => (
          <Reveal key={m.id} delay={(i % 3) * 0.08}>
            <div data-testid={`team-member-${m.id}`}>
              {m.photo ? (
                <div className="frame-clip spotlight aspect-[4/5]">
                  <img src={m.photo} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ) : (
                <PlaceholderPhoto className="aspect-[4/5]" label="Portrait — add in Admin → Team" />
              )}
              <div className="mt-4 border-t border-ink/15 pt-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-2xl tracking-tight">{m.name}</h3>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${
                    m.kind === "Collaborator" ? "border-ochre/60 text-ochre" : "border-ink/25 text-ink/60"
                  }`}>
                    {m.kind === "Collaborator" ? "Collaborator" : "Core team"}
                  </span>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand mt-1.5">{m.role}</p>
                {m.bio && <p className="text-sm text-ink/60 leading-relaxed mt-3">{m.bio}</p>}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function EndActions() {
  return (
    <section data-testid="about-end-actions" className="mt-28 border-t border-ink/15 pt-14">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <Magnetic>
          <Link to="/work" data-testid="about-explore-work-btn"
            className="group inline-flex items-center justify-center gap-3 bg-ink text-paper px-9 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand transition-colors">
            Explore Our Work <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Magnetic>
        <Magnetic>
          <Link to="/contact" data-testid="about-work-with-us-btn"
            className="inline-flex items-center justify-center gap-3 border border-ink px-9 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-brand hover:text-brand transition-colors">
            Work With Us
          </Link>
        </Magnetic>
      </div>
    </section>
  );
}

export default function About() {
  const [team, setTeam] = useState([]);
  const pg = usePage("about");

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const founder = team.find((m) => m.kind === "Founder");
  const members = team.filter((m) => m.kind !== "Founder");

  return (
    <MotionConfig reducedMotion="user">
      <main data-testid="about-page" className="pt-32 pb-24 min-h-screen">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <Overline>About IKSHA</Overline>
          <MaskedLines
            as="h1"
            className="mt-6 max-w-5xl"
            lineClassName="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1]"
            lines={[pg.heading_1 || "Space, art, material,", pg.heading_2 || "craft and making."]}
          />
          <AnchorLinks showTeam={members.length > 0} />
          <WhoWeAre />
          {pg.show_vision !== false && <Vision />}
          {pg.show_founder !== false && <Founder founder={founder} />}
          {members.length > 0 && <Team members={members} />}
          <EndActions />
        </div>
      </main>
    </MotionConfig>
  );
}
